import { pipe } from '@helo/utils'

import { Options } from '../../Options'
import type { QueryOutput } from '../../Query'
import { RenderPipeline } from '../../RenderPipeline'
import { ChartDataParts } from '../ChartData/ChartData.parts'
import type { ChartGridSlice } from './ChartGrid'
import { ChartGridConstants } from './ChartGrid.constants'
import { chartGridOptions } from './ChartGrid.options'

const AXIS_EDGE_TOLERANCE_PX = 1

const isOnEdge = (pixel: number, edge: number): boolean =>
  Math.abs(pixel - edge) <= AXIS_EDGE_TOLERANCE_PX

/**
 * Drop the horizontal grid row on the bottom plot edge (it overlaps the
 * x-axis). Keep the top row — Figma draws a grid line at the max tick.
 */
const rowTicksAwayFromAxes = ({
  ticks,
  yScale,
  innerHeight,
}: {
  ticks: number[] | undefined
  yScale: (value: number) => number
  innerHeight: number
}): number[] | undefined => {
  if (ticks == null) return undefined
  return ticks.filter(tick => {
    const y = yScale(tick)
    if (!Number.isFinite(y)) return false
    return !isOnEdge(y, innerHeight)
  })
}

/**
 * Drop vertical grid columns that sit on the left/right plot edges.
 */
const columnTicksAwayFromAxes = ({
  domain,
  xScale,
  innerWidth,
}: {
  domain: string[]
  xScale: (value: string) => number | undefined
  innerWidth: number
}): string[] =>
  domain.filter(value => {
    const x = xScale(value)
    if (x == null || !Number.isFinite(x)) return false
    return !isOnEdge(x, 0) && !isOnEdge(x, innerWidth)
  })

/** Value-axis columns (horizontal bar charts). */
const columnMeasureTicksAwayFromAxes = ({
  ticks,
  xScale,
  innerWidth,
}: {
  ticks: number[] | undefined
  xScale: (value: number) => number
  innerWidth: number
}): number[] | undefined => {
  if (ticks == null) return undefined
  return ticks.filter(tick => {
    const x = xScale(tick)
    if (!Number.isFinite(x)) return false
    return !isOnEdge(x, 0) && !isOnEdge(x, innerWidth)
  })
}

/** Write which gridlines are visible from `_xAxis` / `_yAxis`. */
const setGrid = () =>
  RenderPipeline.updateOutput(
    (
      _output: {
        contentNode?: QueryOutput['contentNode']
        grid?: ChartGridSlice
      },
      ctx: {
        input: {
          _xAxis?: { showGridlines?: boolean }
          _yAxis?: { showGridlines?: boolean }
        }
        output: {
          contentNode?: QueryOutput['contentNode']
          grid?: ChartGridSlice
        }
      },
    ) => {
      const xAxis = {
        ...(ChartDataParts.getNestedState('_xAxis')(ctx) as {
          showGridlines?: boolean
        }),
        ...ctx.input._xAxis,
      }
      const yAxis = {
        ...(ChartDataParts.getNestedState('_yAxis')(ctx) as {
          showGridlines?: boolean
        }),
        ...ctx.input._yAxis,
      }
      return {
        grid: {
          showXGridlines:
            xAxis.showGridlines ?? ChartGridConstants.defaultShowXGridlines,
          showYGridlines:
            yAxis.showGridlines ?? ChartGridConstants.defaultShowYGridlines,
        },
      }
    },
  )

/** Resolve grid options from override, then content-node state. */
const setupInput = () =>
  RenderPipeline.updateInput((input, ctx) =>
    pipe(
      input,
      Options.fallback(chartGridOptions, ChartDataParts.getChartState()(ctx)),
      Options.applyDefaults(chartGridOptions),
      Options.validate(chartGridOptions),
    ),
  )

/** Same resolution for `_xAxis` gridlines. */
const setup_xAxisInput = () =>
  RenderPipeline.updateInput((input, ctx) => ({
    ...input,
    _xAxis: pipe(
      input._xAxis ?? {},
      Options.fallback(
        chartGridOptions,
        ChartDataParts.getNestedState('_xAxis')(ctx),
      ),
      Options.applyDefaults(chartGridOptions),
    ),
  }))

/** Same resolution for `_yAxis` gridlines. */
const setup_yAxisInput = () =>
  RenderPipeline.updateInput((input, ctx) => ({
    ...input,
    _yAxis: pipe(
      input._yAxis ?? {},
      Options.fallback(
        chartGridOptions,
        ChartDataParts.getNestedState('_yAxis')(ctx),
      ),
      Options.applyDefaults(chartGridOptions),
    ),
  }))

export const ChartGridInputParts = {
  setupInput,
  setup_xAxisInput,
  setup_yAxisInput,
}

export const ChartGridOutputParts = {
  setGrid,
}

export const ChartGridUtils = {
  rowTicksAwayFromAxes,
  columnTicksAwayFromAxes,
  columnMeasureTicksAwayFromAxes,
}
